import { AuthenticatedUser } from "src/utils/AuthenticatedUser";
import axios from "axios";
import {
	GetIsUserAuthenticatedResponse,
	GetUserResponse
} from "src/types/apiResponse";
import { GetPartnersResponse } from "src/types/apiResponse";
import RouteBuilder from "src/utils/routing/RouteBuilder";

declare global {
	interface Window {
		caliperx2?: {
			authentication: {
				state: {
					getUser(): any;
					getIsAuthenticated: () => boolean;
				};
			};
			config: {
				ready: boolean;
			};
		};
	}
}

function isDevExHost(): boolean {
	const hostname = window.location.hostname;
	return hostname.includes('developer');
}

/**
 * Authenticates user and sets up session.
 * Handles both DevEx (developerit) and standard OAuth flows.
 */
export async function handleAuthentication(
	routeBuilder: RouteBuilder,
	setIsAuthenticated: (newIsAuthenticated: boolean) => void,
	setUser: (newUser: AuthenticatedUser | null) => void,
	callback: () => void
): Promise<void> {
	const getIsUserAuthenticatedRoute = routeBuilder.api.getIsUserAuthenticated();
	const getUserRoute = routeBuilder.api.getUser();
	const getPartnersAccessRoute = routeBuilder.api.getAllDashboardsPartners();

	if (isDevExHost()) {
		console.log("== AUTH: Using DevEx CaliperX authentication");
		await handleDevExAuthentication(
			routeBuilder,
			setIsAuthenticated,
			setUser,
			callback,
			getIsUserAuthenticatedRoute,
			getUserRoute,
			getPartnersAccessRoute
		);
	}
	else {
		console.log("== AUTH: Using standard OAuth authentication");
		await handleStandardAuthentication(
			routeBuilder,
			setIsAuthenticated,
			setUser,
			callback,
			getIsUserAuthenticatedRoute,
			getUserRoute,
			getPartnersAccessRoute
		);
	}
}

function waitForCaliperXReady(): Promise<void> {
	return new Promise((resolve, reject) => {
		if (window.caliperx2?.config?.ready) {
			console.log("== AUTH: CaliperX already ready");
			resolve();
			return;
		}

		const timeout = setTimeout(() => {
			console.error("== AUTH: CaliperX library failed to load within 30 seconds");
			reject(new Error("CaliperX library failed to load within 30 seconds"));
		}, 30000);

		const checkInterval = setInterval(() => {
			if (window.caliperx2?.config?.ready) {
				clearInterval(checkInterval);
				clearTimeout(timeout);
				console.log("== AUTH: CaliperX is now ready");
				resolve();
			}
		}, 100);
	});
}

/**
 * Handles DevEx CaliperX authentication flow
 */
async function handleDevExAuthentication(
	routeBuilder: RouteBuilder,
	setIsAuthenticated: (newIsAuthenticated: boolean) => void,
	setUser: (newUser: AuthenticatedUser | null) => void,
	callback: () => void,
	getIsUserAuthenticatedRoute: string,
	getUserRoute: string,
	getPartnersAccessRoute: string
): Promise<void> {
	try {
		console.log("== AUTH: Waiting for CaliperX to be ready...");
		await waitForCaliperXReady();
        const hostname = window.location.origin;
        const currentPath = window.location.pathname;

		if (hostname.includes('developer') && !window.caliperx2.authentication.state.getIsAuthenticated()) {
            let authPath = window.location.origin + "/auth/sign-in?returnTo=" + currentPath;
			console.log("== AUTH: User is not authenticated. Redirecting to DevEx login page, with return to current path: " + currentPath);
            window.location.assign(authPath);
            return;
		}
		try {
			const isUserAuthenticatedRes = await axios.get<GetIsUserAuthenticatedResponse>(
				getIsUserAuthenticatedRoute,
				{ withCredentials: true }
			);

			if (isUserAuthenticatedRes?.data?.status && isUserAuthenticatedRes.data.status === "OK") {
				console.log("== AUTH: User authenticated on backend. Setting user role and accesses...");
				await fetchUserAndPartners(
					getUserRoute,
					getPartnersAccessRoute,
					setUser,
					callback,
					setIsAuthenticated,
					routeBuilder
				);
			}
		} catch (e: any) {
			if (e.response && e.response.status === 401) {
				console.log("== AUTH: Existing session not found on backend, redirecting to parse token...");
				const analyticsPrefix = "/partnerships/analytics";
				const analyticsSpecificPath = currentPath.replace(analyticsPrefix, "");
				window.location.assign(hostname + "/partnerships/analytics/api/login/partner_portal/jwt?returnTo=" + analyticsSpecificPath);
			} else {
				console.error("== AUTH: Uncaught exception - Failed to get user authentication status.");
				console.error(e);
			}
		}
	} catch (error) {
		console.error("== AUTH: Error in DevEx authentication flow");
		console.error(error);
	}
}

/**
 * Handles standard OAuth authentication flow (legacy host)
 */
async function handleStandardAuthentication(
	routeBuilder: RouteBuilder,
	setIsAuthenticated: (newIsAuthenticated: boolean) => void,
	setUser: (newUser: AuthenticatedUser | null) => void,
	callback: () => void,
	getIsUserAuthenticatedRoute: string,
	getUserRoute: string,
	getPartnersAccessRoute: string
): Promise<void> {
	console.log("== AUTH: Checking backend authentication status...");

	try {
		const isUserAuthenticatedRes = await axios.get<GetIsUserAuthenticatedResponse>(
			getIsUserAuthenticatedRoute,
			{ withCredentials: true }
		);

		if (isUserAuthenticatedRes?.data?.status && isUserAuthenticatedRes.data.status === "OK") {
			console.log("== AUTH: User authenticated on backend. Setting user role and accesses...");
			await fetchUserAndPartners(
				getUserRoute,
				getPartnersAccessRoute,
				setUser,
				callback,
				setIsAuthenticated,
				routeBuilder
			);
		} else {
			console.log("== AUTH: User not authenticated. Redirecting to login...");
			goToAuthenticationPage(setUser, setIsAuthenticated);
		}
	} catch (e: any) {
		if (e.response && e.response.status === 401) {
			console.log("== AUTH: User not authenticated (401). Redirecting to legacy login page...");
			goToAuthenticationPage(setUser, setIsAuthenticated);
		} else {
			console.error("== AUTH: Uncaught exception - Failed to get user authentication status.");
			console.error(e);
		}
	}
}

/**
 * Fetches user info and partner access, then sets up authenticated state.
 */
async function fetchUserAndPartners(
	getUserRoute: string,
	getPartnersAccessRoute: string,
	setUser: (newUser: AuthenticatedUser | null) => void,
	callback: () => void,
	setIsAuthenticated: (newIsAuthenticated: boolean) => void,
	routeBuilder: RouteBuilder
): Promise<void> {
	try {
		const [userRes, partnerAccessRes] = await Promise.all([
			axios.get<GetUserResponse>(getUserRoute, { withCredentials: true }),
			axios
				.get<GetPartnersResponse>(getPartnersAccessRoute, { withCredentials: true })
				.catch((partnerError) => {
					console.warn("== AUTH: Failed to get partners access, setting user without partner data");
					console.error(partnerError);
					return null;
				})
		]);

		if (userRes && userRes.data && userRes.data.user) {
			if (partnerAccessRes && partnerAccessRes.data) {
				setUser(new AuthenticatedUser(userRes.data.user, partnerAccessRes.data));
			} else {
				setUser(new AuthenticatedUser(userRes.data.user, null));
			}
		}
	} catch (e) {
		console.error("== AUTH: Failed to get user info");
		console.error(e);
		setUser(null);
	} finally {
		callback();
		setIsAuthenticated(true);
	}
}

function goToAuthenticationPage(
	setUser: (user: AuthenticatedUser | null) => void,
	setIsAuthenticated: (isAuthenticated: boolean) => void
): void {
	if (window.location.pathname.toLowerCase().localeCompare("/login") != 0) {
		console.log("Auth: Redirecting to login page");
		window.location.assign("/login");
	}
}